import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Select, DatePicker, Button, Table, Tag, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import * as XLSX from "xlsx";
import { getStatesByRole, getCleaningAuditRoleId, getAuditTrackingReport } from "./cleaningAuditApi";

const { Option } = Select;
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// audit_status per facility is an object keyed by month number, each value
// [managerDone, staffDone] — matches auditScheduled/TrackingReport.js.
const TrackingReport = () => {
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [year, setYear] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const roleId = await getCleaningAuditRoleId();
        const { error, result } = await getStatesByRole(roleId);
        setStates(!error && Array.isArray(result?.result) ? result.result : []);
      } catch (e) {
        setStates([]);
        message.error({ key: "cleaning-audit-connect", content: "Couldn't connect to Cleaning Audit. Please try again shortly." });
      }
    })();
  }, []);

  const monthKeys = useMemo(() => {
    const keys = new Set();
    rows.forEach((r) => Object.keys(r.audit_status || {}).forEach((k) => keys.add(Number(k))));
    return Array.from(keys).sort((a, b) => a - b);
  }, [rows]);

  const columns = useMemo(
    () => [
      { title: "Facility Name", dataIndex: "facility_name", key: "facility_name", fixed: "left" },
      ...monthKeys.map((m) => ({
        title: MONTH_NAMES[m - 1] || m,
        children: [
          {
            title: "Audit",
            key: `audit_${m}`,
            render: (_, row) => {
              const done = row.audit_status?.[m]?.[0];
              return <Tag color={done ? "green" : "default"}>{done ? "Done" : "-"}</Tag>;
            },
          },
          {
            title: "Rectification",
            key: `rect_${m}`,
            render: (_, row) => {
              const done = row.audit_status?.[m]?.[1];
              return <Tag color={done ? "green" : "default"}>{done ? "Done" : "-"}</Tag>;
            },
          },
        ],
      })),
    ],
    [monthKeys]
  );

  const search = useCallback(async () => {
    if (!stateId) {
      message.error("Please select a state first.");
      return;
    }
    setLoading(true);
    setSearched(true);
    const { error, result } = await getAuditTrackingReport({ year: Number(year.format("YYYY")), state_id: Number(stateId) });
    const data = !error && Array.isArray(result?.result) ? result.result : [];
    setRows(data.slice().sort((a, b) => a.facility_name.localeCompare(b.facility_name)));
    setLoading(false);
  }, [stateId, year]);

  const exportExcel = useCallback(() => {
    const header = ["Facility Name", ...monthKeys.flatMap((m) => [`${MONTH_NAMES[m - 1]} Audit`, `${MONTH_NAMES[m - 1]} Rectification`])];
    const data = rows.map((r) => [
      r.facility_name,
      ...monthKeys.flatMap((m) => [r.audit_status?.[m]?.[0] ? "Done" : "-", r.audit_status?.[m]?.[1] ? "Done" : "-"]),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tracking Report");
    const stateName = states.find((s) => s.state_id === stateId)?.state_name || "All";
    XLSX.writeFile(wb, `${year.format("YYYY")}_${stateName.replace(/\s+/g, "_")}_Cleaning_Audit_Report_Tracking.xlsx`);
  }, [rows, monthKeys, year, stateId, states]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Year</div>
            <DatePicker
              picker="year"
              value={year}
              onChange={(d) => d && setYear(d)}
              allowClear={false}
              disabledDate={(d) => d && d > moment().endOf("year")}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a state"
              style={{ width: 220 }}
              value={stateId}
              onChange={setStateId}
            >
              {states.map((s) => (
                <Option key={s.state_id} value={s.state_id}>{s.state_name}</Option>
              ))}
            </Select>
          </div>
          <Button type="primary" loading={loading} onClick={search}>Search</Button>
          {rows.length > 0 && (
            <Button icon={<DownloadOutlined />} onClick={exportExcel}>Export</Button>
          )}
        </div>
      </div>

      {searched && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, margin: "8px 0 12px" }}>Audit Tracking Report</div>
          <Table
            rowKey="facility_id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            scroll={{ x: true }}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: "No records for this year/state." }}
          />
        </div>
      )}
    </div>
  );
};

export default memo(TrackingReport);
